/**
 * hype-visitor-counter — Social proof visitor count badge web component.
 *
 * Attributes (all strings, passed from Liquid):
 *   view-count        — number of recent viewers (default: "0")
 *   threshold         — only show when views >= this number (default: "5")
 *   message-template  — template with {count} placeholder (default: "{count} people viewed this today")
 *   bg-color          — background CSS color
 *   text-color        — text CSS color
 *   accent-color      — count number accent color
 *   display-style     — "badge" | "inline" (default: "badge")
 */

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

@customElement('hype-visitor-counter')
export class HypeVisitorCounter extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: inherit;
      font-size: 16px; /* Pin against Dawn's 62.5% rem trick */
      /* Reserve 40px to match the Liquid template host element min-height,
         preventing CLS while the component initialises. */
      min-height: 40px;
    }
    .badge {
      display: inline-flex;
      align-items: center;
      gap: 0.4em;
      padding: 0.4em 0.75em;
      background: var(--hype-bg, var(--color-background, #f3f4f6));
      color: var(--hype-text, var(--color-foreground, #374151));
      border: 1px solid color-mix(in srgb, var(--hype-text, var(--color-foreground, #374151)) 25%, transparent);
      border-radius: 6px;
      font-size: 0.875em;
      font-weight: 500;
      line-height: 1.4;
      min-height: 40px;
      box-sizing: border-box;
    }
    .icon {
      font-size: 1em;
      flex-shrink: 0;
    }
    .count {
      font-size: 1em;
      font-weight: 700;
      color: var(--hype-accent, var(--color-accent, #6b7280));
    }

    /* Mobile-first responsive — tighter padding on narrow viewports. */
    @media (max-width: 480px) {
      .badge {
        padding: 0.35em 0.6em;
        font-size: 0.8em;
        gap: 0.3em;
      }
      .count {
        font-size: 0.9em;
      }
    }

    /* Very narrow phones. */
    @media (max-width: 320px) {
      .badge {
        padding: 0.3em 0.5em;
        font-size: 0.75em;
      }
      .count {
        font-size: 0.85em;
      }
    }

    /* ── Inline display style ─────────────────────────────────────── */
    :host([display-style="inline"]) .badge {
      background: none;
      border: none;
      padding: 0;
      min-height: auto;
      font-size: 0.875em;
      border-radius: 0;
    }
    :host([display-style="inline"]) .icon {
      display: none;
    }

    /* ── Number change micro-animation ───────────────────────────── */
    @keyframes hype-num-pulse {
      0%   { transform: scale(1); }
      50%  { transform: scale(1.15); }
      100% { transform: scale(1); }
    }
    .count.num-pulse {
      animation: hype-num-pulse 180ms ease-out both;
      display: inline-block;
    }

    /* ── Entry animation ─────────────────────────────────────────── */
    @keyframes hype-entry {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .badge.entering {
      animation: hype-entry 350ms ease-out both;
    }

    /* ── Powered by Hype branding ────────────────────────────── */
    .hype-branding {
      display: block;
      padding: 0.1em 0;
      background: var(--hype-bg, transparent);
    }
    .hype-branding a {
      font-size: 0.625em;
      color: var(--hype-text, var(--color-foreground, #374151));
      opacity: 0.4;
      text-decoration: none;
      letter-spacing: 0.02em;
    }
    .hype-branding a:hover {
      opacity: 0.65;
      text-decoration: underline;
    }

    @media (prefers-reduced-motion: reduce) {
      :host, .badge {
        animation: none !important;
        transition: none !important;
      }
      .badge.entering {
        animation: none !important;
        opacity: 1 !important;
        transform: none !important;
      }
      .count.num-pulse {
        animation: none !important;
      }
    }
  `;

  @property({ attribute: 'view-count' }) viewCount = '0';
  @property({ attribute: 'threshold' }) threshold = '5';
  @property({ attribute: 'message-template' }) messageTemplate = '{count} people viewed this today';
  @property({ attribute: 'bg-color' }) bgColor = '#f3f4f6';
  @property({ attribute: 'text-color' }) textColor = '#374151';
  @property({ attribute: 'accent-color' }) accentColor = '#6b7280';
  @property({ attribute: 'display-style' }) displayStyle = 'badge';
  @property({ attribute: 'hide-branding' }) hideBranding = 'false';
  @property({ attribute: 'custom-css' }) customCss = '';
  @property({ attribute: 'api-url' }) apiUrl = '';

  @state() private _entering = false;
  @state() private _liveCount: number | null = null;
  @state() private _numPulsing = false;

  private _customStyleEl: HTMLStyleElement | null = null;
  private _pollTimer: ReturnType<typeof setInterval> | null = null;
  private _visibilityHandler: (() => void) | null = null;

  connectedCallback() {
    super.connectedCallback();
    // Trigger entry animation on first render.
    this._entering = true;
    setTimeout(() => { this._entering = false; }, 350);

    if (this.apiUrl) {
      this._startPolling();
      this._fetchCount();
      this._visibilityHandler = () => {
        if (document.visibilityState === 'visible') {
          this._fetchCount();
          this._startPolling();
        } else {
          this._stopPolling();
        }
      };
      document.addEventListener('visibilitychange', this._visibilityHandler);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stopPolling();
    if (this._visibilityHandler !== null) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
  }

  override updated(changed: Map<string, unknown>) {
    super.updated(changed);
    if (changed.has('customCss')) {
      this._applyCustomCss();
    }
  }

  private _startPolling() {
    this._stopPolling();
    if (!this.apiUrl || document.visibilityState !== 'visible') return;
    this._pollTimer = setInterval(() => this._fetchCount(), 30_000);
  }

  private _stopPolling() {
    if (this._pollTimer !== null) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  }

  private async _fetchCount(): Promise<void> {
    if (!this.apiUrl) return;
    try {
      const res = await fetch(this.apiUrl, { credentials: 'omit' });
      if (!res.ok) return;
      const data = await res.json() as { view_count?: number; count?: number };
      const newCount = data.view_count ?? data.count ?? null;
      if (newCount !== null && newCount !== this._liveCount) {
        const prevCount = this._liveCount ?? (parseInt(this.viewCount, 10) || 0);
        this._liveCount = newCount;
        if (newCount !== prevCount) {
          this._triggerNumPulse();
        }
      }
    } catch {
      // Silently fail — keep displaying current count.
    }
  }

  private _triggerNumPulse() {
    this._numPulsing = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._numPulsing = true;
        setTimeout(() => { this._numPulsing = false; }, 200);
      });
    });
  }

  private _applyCustomCss() {
    if (!this.shadowRoot) return;
    if (this.customCss) {
      if (!this._customStyleEl) {
        this._customStyleEl = document.createElement('style');
        this.shadowRoot.appendChild(this._customStyleEl);
      }
      this._customStyleEl.textContent = this.customCss;
    } else if (this._customStyleEl) {
      this._customStyleEl.remove();
      this._customStyleEl = null;
    }
  }

  private _renderBranding() {
    if (this.hideBranding === 'true') return html``;
    return html`
      <div class="hype-branding">
        <a href="https://tehmop.com/hype" target="_blank" rel="noopener noreferrer">Powered by Hype</a>
      </div>
    `;
  }

  render() {
    const views = this._liveCount ?? (parseInt(this.viewCount, 10) || 0);
    const limit = parseInt(this.threshold, 10) || 5;

    // Below threshold or zero — render nothing.
    if (views < limit) return nothing;

    this.style.setProperty('--hype-bg', this.bgColor);
    this.style.setProperty('--hype-text', this.textColor);
    this.style.setProperty('--hype-accent', this.accentColor);

    // Split the template around {count} and render the count in a <strong>.
    const parts = this.messageTemplate.split('{count}');
    const before = parts[0] ?? '';
    const after = parts.slice(1).join('{count}');

    return html`
      <div class="badge${this._entering ? ' entering' : ''}">
        <span class="icon">\u{1F441}</span>
        <span>${before}<strong class="count${this._numPulsing ? ' num-pulse' : ''}">${views}</strong>${after}</span>
      </div>
      ${this._renderBranding()}
    `;
  }
}
